import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Search,
  X,
  RefreshCw,
  MessageSquare,
  Clock,
  CheckCircle2,
  Loader2,
  ShieldCheck,
  Trash2,
  CalendarDays,
  Filter,
  AlertTriangle,
  Tag,
  AlertCircle,
  Flag,
  User,
  Globe,
} from 'lucide-react';
import { storage } from '../../Shared/Utils/authStorage';
import { Alert, AlertProps } from '../../Shared/Utils/Alert';
import Pagination from '../../Shared/Pagination/Pagination';
import { PaginationResponse } from '../../Shared/Pagination/PaginationResponse';
import { deleteFeedback, getAdminFeedbackFiltered } from '../Services/AdminService';
import { FeedbackResponseDto, FeedbackStats } from '../Types/FeedbackType';
import AdminFeedbackFormModal from '../Components/AdminFeedbackFormModal';

const PAGE_SIZE = 8;

const emptyStats: FeedbackStats = { total: 0, open: 0, inProgress: 0, resolved: 0 };

const CATEGORY_OPTIONS = [
  { label: 'All Categories', value: '' },
  { label: 'Bug', value: 'BUG' },
  { label: 'Support', value: 'SUPPORT' },
  { label: 'Feature Request', value: 'FEATURE_REQUEST' },
  { label: 'General', value: 'GENERAL' },
  { label: 'Complaint', value: 'COMPLAINT' },
  { label: 'Other', value: 'OTHER' },
];

const PRIORITY_OPTIONS = [
  { label: 'All Priorities', value: '' },
  { label: 'Low', value: 'LOW' },
  { label: 'Medium', value: 'MEDIUM' },
  { label: 'High', value: 'HIGH' },
  { label: 'Critical', value: 'CRITICAL' },
];

const STATUS_OPTIONS = ['ALL', 'OPEN', 'IN_PROGRESS', 'RESOLVED', 'CLOSED', 'REOPEN'];

const SOURCE_OPTIONS = [
  { label: 'All Sources', value: '' },
  { label: 'Web Direct', value: 'WEB-DIRECT' },
  { label: 'Candidate', value: 'CANDIDATE' },
  { label: 'Recruiter', value: 'RECRUITER' },
];

const PRIORITY_STYLES: Record<string, string> = {
  LOW: 'border-slate-700 bg-slate-950 text-slate-400',
  MEDIUM: 'border-sky-500/30 bg-sky-500/10 text-sky-400',
  HIGH: 'border-amber-500/30 bg-amber-500/10 text-amber-400',
  CRITICAL: 'border-rose-500/30 bg-rose-500/10 text-rose-400',
};

const STATUS_STYLES: Record<string, string> = {
  OPEN: 'border-sky-500/30 bg-sky-500/10 text-sky-400',
  IN_PROGRESS: 'border-amber-500/30 bg-amber-500/10 text-amber-400',
  RESOLVED: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400',
  CLOSED: 'border-slate-700 bg-slate-950 text-slate-400',
  REOPEN: 'border-violet-500/30 bg-violet-500/10 text-violet-400',
};

const SOURCE_STYLES: Record<string, string> = {
  'WEB-DIRECT': 'border-teal-500/30 bg-teal-500/10 text-teal-400',
  CANDIDATE: 'border-cyan-500/30 bg-cyan-500/10 text-cyan-400',
  RECRUITER: 'border-violet-500/30 bg-violet-500/10 text-violet-400',
};

const prettifyCategory = (value?: string) =>
  value ? value.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : '—';

const prettifyStatus = (value?: string) =>
  value ? value.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : '—';

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const StatCard = ({
  label,
  value,
  icon,
  accent,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  accent: string;
}) => (
  <div className="relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
    <div className="flex items-center justify-between">
      <span className={`flex h-9 w-9 items-center justify-center rounded-xl border ${accent}`}>{icon}</span>
      <span className="text-2xl font-black text-slate-100">{value}</span>
    </div>
    <p className="mt-2 text-[11px] font-bold uppercase tracking-widest text-slate-400">{label}</p>
  </div>
);

function FeedbackPage() {
  const [feedbacks, setFeedbacks] = useState<FeedbackResponseDto[]>([]);
  const [pagination, setPagination] =
    useState<PaginationResponse<FeedbackResponseDto> | null>(null);
  const [stats, setStats] = useState<FeedbackStats>(emptyStats);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [alert, setAlert] = useState<AlertProps | null>(null);

  const [category, setCategory] = useState('');
  const [priority, setPriority] = useState('');
  const [status, setStatus] = useState('ALL');
  const [source, setSource] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [editingFeedback, setEditingFeedback] = useState<FeedbackResponseDto | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<FeedbackResponseDto | null>(null);
  const [deleting, setDeleting] = useState(false);

  const adminId = storage.getUserId() ?? '';

  // Debounce the search box so we don't hit the API on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchFeedbacks = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getAdminFeedbackFiltered(
        {
          search: debouncedSearch || undefined,
          category: category || undefined,
          priority: priority || undefined,
          status: status !== 'ALL' ? status : undefined,
          source: source || undefined,
          fromDate: fromDate || undefined,
          toDate: toDate || undefined,
        },
        page,
        PAGE_SIZE
      );
      if (response.success && response.data) {
        const pageData = response.data.page;
        setFeedbacks(pageData.content ?? []);
        setStats(response.data.stats ?? emptyStats);
        setPagination(pageData);
        if (pageData.totalPages > 0 && page >= pageData.totalPages) {
          setPage(pageData.totalPages - 1);
        }
      } else {
        setFeedbacks([]);
        setStats(emptyStats);
        setPagination(null);
        setError(response.message || 'Could not load feedback.');
      }
    } catch (err) {
      console.error('Failed to load feedback', err);
      setFeedbacks([]);
      setStats(emptyStats);
      setPagination(null);
      setError('An unexpected error occurred while loading feedback.');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, category, priority, status, source, fromDate, toDate, page]);

  useEffect(() => {
    fetchFeedbacks();
  }, [fetchFeedbacks]);

  // Reset to the first page whenever the active filters change.
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, category, priority, status, source, fromDate, toDate]);

  const resetFilters = () => {
    setSearch('');
    setCategory('');
    setPriority('');
    setStatus('ALL');
    setSource('');
    setFromDate('');
    setToDate('');
  };

  const hasActiveFilters =
    Boolean(search) || Boolean(category) || Boolean(priority) || status !== 'ALL' || Boolean(source) || Boolean(fromDate) || Boolean(toDate);

  const openReview = (feedback: FeedbackResponseDto) => {
    setEditingFeedback(feedback);
    setModalOpen(true);
  };

  const handleSaved = (message: string) => {
    setAlert({ variant: 'success', title: 'Success', message, autoCloseMs: 4000, onClose: () => setAlert(null) });
    fetchFeedbacks();
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const response = await deleteFeedback(deleteTarget.id);
      if (response.success) {
        setAlert({
          variant: 'success',
          title: 'Deleted',
          message: 'Feedback removed successfully.',
          autoCloseMs: 4000,
          onClose: () => setAlert(null),
        });
        setDeleteTarget(null);
        fetchFeedbacks();
      } else {
        setAlert({
          variant: 'error',
          title: 'Delete failed',
          message: response.message || 'Could not remove the feedback.',
          autoCloseMs: 5000,
          onClose: () => setAlert(null),
        });
      }
    } catch (err) {
      console.error('Failed to delete feedback', err);
      setAlert({
        variant: 'error',
        title: 'System error',
        message: 'Something went wrong. Please try again.',
        autoCloseMs: 5000,
        onClose: () => setAlert(null),
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      {alert && <Alert {...alert} />}

      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
            <MessageSquare size={22} />
          </span>
          <div>
            <h1 className="text-xl font-black tracking-tight text-slate-100 md:text-2xl">Feedback</h1>
            <p className="text-xs font-semibold text-slate-400 md:text-sm">
              Review and resolve feedback submitted by users
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={fetchFeedbacks}
            disabled={loading}
            className="flex items-center justify-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-4 py-2.5 text-xs font-black uppercase tracking-widest text-cyan-400 transition hover:border-cyan-500/40 disabled:opacity-50"
          >
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          label="Total Feedback"
          value={stats.total}
          icon={<MessageSquare size={16} />}
          accent="border-cyan-500/30 bg-cyan-500/10 text-cyan-400"
        />
        <StatCard
          label="Open"
          value={stats.open}
          icon={<Clock size={16} />}
          accent="border-sky-500/30 bg-sky-500/10 text-sky-400"
        />
        <StatCard
          label="In Progress"
          value={stats.inProgress}
          icon={<Loader2 size={16} />}
          accent="border-amber-500/30 bg-amber-500/10 text-amber-400"
        />
        <StatCard
          label="Resolved / Closed"
          value={stats.resolved}
          icon={<CheckCircle2 size={16} />}
          accent="border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
        />
      </div>

      {/* Filters */}
      <div className="mb-6 rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
        <div className="mb-3 flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-slate-400">
          <Filter size={14} /> Filters
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-7">
          <div className="relative xl:col-span-2">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search subject, message, reporter..."
              className="w-full rounded-xl border border-slate-800 bg-slate-950 py-2.5 pl-9 pr-9 text-sm text-slate-200 placeholder-slate-600 outline-none transition focus:border-cyan-500/50"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
              >
                <X size={15} />
              </button>
            )}
          </div>

          <div className="relative">
            <Tag size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full appearance-none rounded-xl border border-slate-800 bg-slate-950 py-2.5 pl-9 pr-8 text-sm text-slate-200 outline-none transition [color-scheme:dark] focus:border-cyan-500/50"
            >
              {CATEGORY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value} className="bg-slate-900 text-slate-100">
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="relative">
            <AlertCircle size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              className="w-full appearance-none rounded-xl border border-slate-800 bg-slate-950 py-2.5 pl-9 pr-8 text-sm text-slate-200 outline-none transition [color-scheme:dark] focus:border-cyan-500/50"
            >
              {PRIORITY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value} className="bg-slate-900 text-slate-100">
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="relative">
            <Globe size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <select
              value={source}
              onChange={(e) => setSource(e.target.value)}
              title="Filter by source"
              className="w-full appearance-none rounded-xl border border-slate-800 bg-slate-950 py-2.5 pl-9 pr-8 text-sm text-slate-200 outline-none transition [color-scheme:dark] focus:border-cyan-500/50"
            >
              {SOURCE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value} className="bg-slate-900 text-slate-100">
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="relative">
            <CalendarDays size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-full rounded-xl border border-slate-800 bg-slate-950 py-2.5 pl-9 pr-3 text-sm text-slate-200 outline-none transition [color-scheme:dark] focus:border-cyan-500/50"
            />
          </div>

          <div className="relative">
            <CalendarDays size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="date"
              value={toDate}
              min={fromDate || undefined}
              onChange={(e) => setToDate(e.target.value)}
              className="w-full rounded-xl border border-slate-800 bg-slate-950 py-2.5 pl-9 pr-3 text-sm text-slate-200 outline-none transition [color-scheme:dark] focus:border-cyan-500/50"
            />
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-3">
          {STATUS_OPTIONS.map((option) => (
            <button
              key={option}
              onClick={() => setStatus(option)}
              className={`rounded-full border px-3 py-1.5 text-[10px] font-black uppercase tracking-widest transition ${
                status === option
                  ? 'border-cyan-500/40 bg-cyan-500/10 text-cyan-300'
                  : 'border-slate-800 bg-slate-950 text-slate-400 hover:text-slate-200'
              }`}
            >
              {option === 'ALL' ? 'All Status' : prettifyStatus(option)}
            </button>
          ))}

          {hasActiveFilters && (
            <button
              onClick={resetFilters}
              className="ml-auto flex items-center gap-1.5 rounded-full border border-slate-800 bg-slate-950 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-rose-400 hover:border-rose-500/40"
            >
              <X size={12} /> Clear
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="space-y-4">
          {[0, 1].map((idx) => (
            <div key={idx} className="animate-pulse rounded-2xl border border-slate-800 bg-slate-900/40 p-5">
              <div className="mb-4 h-5 w-48 rounded bg-slate-800" />
              <div className="space-y-3">
                {[0, 1, 2].map((row) => (
                  <div key={row} className="h-4 w-full rounded bg-slate-800/70" />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="rounded-2xl border border-rose-500/20 bg-rose-500/5 py-16 text-center text-sm font-bold text-rose-400">
          {error}
        </div>
      ) : feedbacks.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-700 bg-slate-900/30 py-20 text-center">
          <span className="mb-2 block text-2xl">🗒️</span>
          <p className="text-xs font-black uppercase tracking-widest text-slate-400">No feedback found</p>
          {hasActiveFilters && (
            <button onClick={resetFilters} className="mt-3 text-xs font-bold text-cyan-400 hover:underline">
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
          <div className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/40">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-800 bg-slate-900/70 px-5 py-4">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-800 bg-slate-950 text-cyan-400">
                  <MessageSquare size={18} />
                </span>
                <div>
                  <h3 className="text-sm font-black tracking-tight text-slate-100 md:text-base">Feedback History</h3>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                    {pagination?.totalElements ?? feedbacks.length} item{(pagination?.totalElements ?? feedbacks.length) === 1 ? '' : 's'}
                  </p>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[1140px] border-collapse text-left">
                <thead>
                  <tr className="border-b border-slate-800 text-[10px] font-black uppercase tracking-widest text-slate-500">
                    <th className="px-5 py-3">Subject</th>
                    <th className="px-5 py-3">Category</th>
                    <th className="px-5 py-3">Source</th>
                    <th className="px-5 py-3">Priority</th>
                    <th className="px-5 py-3">Status</th>
                    <th className="px-5 py-3">Reported By</th>
                    <th className="px-5 py-3">Created</th>
                    <th className="px-5 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {feedbacks.map((item) => (
                    <tr
                      key={item.id}
                      className="border-b border-slate-800/60 transition last:border-0 hover:bg-slate-800/20"
                    >
                      <td className="px-5 py-4">
                        <p className="text-sm font-bold text-slate-100">{item.subject || '—'}</p>
                        {item.message && (
                          <p className="mt-0.5 line-clamp-1 max-w-[280px] text-[11px] text-slate-500">{item.message}</p>
                        )}
                      </td>

                      <td className="px-5 py-4">
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-300">
                          <Tag size={12} className="text-slate-500" />
                          {prettifyCategory(item.category)}
                        </span>
                      </td>

                      <td className="px-5 py-4">
                        <span
                          className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${
                            SOURCE_STYLES[item.sourceFrom ?? ''] ?? 'border-slate-700 bg-slate-950 text-slate-400'
                          }`}
                        >
                          <Globe size={11} /> {item.sourceFrom || '—'}
                        </span>
                      </td>

                      <td className="px-5 py-4">
                        <span
                          className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${
                            PRIORITY_STYLES[item.feedbackPriority] ?? 'border-slate-700 bg-slate-950 text-slate-400'
                          }`}
                        >
                          <Flag size={11} /> {item.feedbackPriority || '—'}
                        </span>
                      </td>

                      <td className="px-5 py-4">
                        <span
                          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${
                            STATUS_STYLES[item.status] ?? 'border-slate-700 bg-slate-950 text-slate-400'
                          }`}
                        >
                          <span className="h-1.5 w-1.5 rounded-full bg-current" />
                          {prettifyStatus(item.status)}
                        </span>
                      </td>

                      <td className="px-5 py-4">
                        <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-300">
                          <User size={12} className="text-slate-500" />
                          {item.reportedName || '—'}
                        </span>
                      </td>

                      <td className="px-5 py-4">
                        <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-300">
                          <CalendarDays size={12} className="text-slate-500" />
                          {formatDate(item.createdAt)}
                        </span>
                      </td>

                      <td className="px-5 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openReview(item)}
                            title="Review feedback"
                            className="flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-950 px-3 py-1.5 text-[11px] font-black uppercase tracking-widest text-cyan-400 transition hover:border-cyan-500/40"
                          >
                            <ShieldCheck size={12} /> Review
                          </button>
                          <button
                            onClick={() => setDeleteTarget(item)}
                            className="flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-950 px-3 py-1.5 text-[11px] font-black uppercase tracking-widest text-rose-400 transition hover:border-rose-500/40"
                          >
                            <Trash2 size={12} /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {pagination && pagination.totalElements > 0 && (
            <div className="rounded-2xl border border-slate-800 bg-slate-900/40 px-4 py-2">
              <Pagination
                page={pagination.page}
                size={pagination.size}
                totalElements={pagination.totalElements}
                totalPages={pagination.totalPages}
                first={pagination.first}
                last={pagination.last}
                hasNext={pagination.hasNext}
                hasPrevious={pagination.hasPrevious}
                setPage={setPage}
              />
            </div>
          )}
        </motion.div>
      )}

      {/* Review modal */}
      <AdminFeedbackFormModal
        isOpen={modalOpen}
        feedback={editingFeedback}
        currentUserId={adminId}
        onClose={() => setModalOpen(false)}
        onSaved={handleSaved}
      />

      {/* Delete confirmation */}
      {deleteTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
          onClick={() => !deleting && setDeleteTarget(null)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-2xl"
          >
            <div className="flex items-start gap-4">
              <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-400">
                <AlertTriangle size={20} />
              </span>
              <div>
                <h3 className="text-base font-black tracking-tight text-slate-100">Delete feedback?</h3>
                <p className="mt-1 text-sm text-slate-400">
                  This will permanently remove{' '}
                  <span className="font-bold text-slate-200">"{deleteTarget.subject}"</span>. This action cannot be
                  undone.
                </p>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="rounded-xl px-4 py-2.5 text-xs font-black uppercase tracking-widest text-slate-400 transition hover:text-slate-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="flex items-center gap-2 rounded-xl bg-rose-500 px-5 py-2.5 text-xs font-black uppercase tracking-widest text-white transition hover:bg-rose-400 disabled:opacity-50"
              >
                {deleting ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                {deleting ? 'Deleting' : 'Delete'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

export default FeedbackPage;
